/* Assets: ticket-timer.js
   90PlusTickets - Cart hold timer (20 min + hidden 5 min grace)
*/
console.log("✅ ticket-timer.js loaded");
(() => {
  const SETTINGS = {
    softMinutes: 20,
    hiddenGraceMinutes: 5,
    storageKey: "pt_cart_timer_v1",
    tickMs: 250,
    syncCartMs: 4000,
    selectors: {
      headerBadge: "[data-pt-timer='header']",
      drawerBadge: "[data-pt-timer='drawer']",
      cartPageBadge: "[data-pt-timer='cartpage']",
    },
    // When we remove items after hard expiry, we only remove "ticket items"
    // i.e., not gift cards, and not items marked with line-item property _timer_exempt=1
    exemptPropertyKey: "_timer_exempt",
  };

  // ---------- Utilities ----------
  const now = () => Date.now();

  function safeJsonParse(str) {
    try { return JSON.parse(str); } catch { return null; }
  }

  function readState() {
    const raw = localStorage.getItem(SETTINGS.storageKey);
    const s = safeJsonParse(raw);
    if (!s || typeof s !== "object") return null;
    if (!s.softExpiresAt || !s.hardExpiresAt) return null;
    return s;
  }

  function writeState(state) {
    localStorage.setItem(SETTINGS.storageKey, JSON.stringify(state));
  }

  function clearState() {
    localStorage.removeItem(SETTINGS.storageKey);
  }

  function formatTime(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  function setBadgeText(text) {
    document.querySelectorAll(SETTINGS.selectors.headerBadge + ", " + SETTINGS.selectors.drawerBadge + ", " + SETTINGS.selectors.cartPageBadge)
      .forEach(el => {
        el.textContent = text;
        el.closest(".pt-timer-wrap")?.classList.toggle("is-hidden", !text);
      });
  }

  function showBadges() {
    document.querySelectorAll(".pt-timer-wrap").forEach(w => w.classList.remove("is-hidden"));
  }

  function hideBadges() {
    document.querySelectorAll(".pt-timer-wrap").forEach(w => w.classList.add("is-hidden"));
  }

  // ---------- Cart logic ----------
  async function fetchCart() {
    const res = await fetch("/cart.js", { headers: { "Accept": "application/json" }, cache: "no-store" });
    if (!res.ok) throw new Error("Failed to fetch /cart.js");
    return res.json();
  }

  function isTicketItem(item) {
    if (!item) return false;
    if (item.gift_card) return false;

    const props = item.properties || {};
    const exempt = props[SETTINGS.exemptPropertyKey];
    if (String(exempt) === "1" || String(exempt).toLowerCase() === "true") return false;

    return true;
  }

  function hasAnyTicketItems(cart) {
    return (cart?.items || []).some(isTicketItem);
  }

  async function removeTicketItemsFromCart() {
    const cart = await fetchCart();
    const ticketLines = [];
    (cart.items || []).forEach((item, idx) => {
      if (isTicketItem(item)) {
        // cart/change.js uses 1-based line index
        ticketLines.push(idx + 1);
      }
    });

    if (!ticketLines.length) return;

    // Remove lines sequentially to avoid race conditions
    for (const line of ticketLines) {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ line, quantity: 0 }),
      });
    }

    // After removal, ask Dawn to refresh sections if possible
    document.dispatchEvent(new CustomEvent("pt:cart:changed"));
  }

  // ---------- Timer state machine ----------
  function ensureStarted() {
    const existing = readState();
    if (existing) return existing;

    const startAt = now();
    const softExpiresAt = startAt + SETTINGS.softMinutes * 60 * 1000;
    const hardExpiresAt = softExpiresAt + SETTINGS.hiddenGraceMinutes * 60 * 1000;

    const state = {
      startAt,
      softExpiresAt,
      hardExpiresAt,
      hardActionDone: false,
    };
    writeState(state);
    return state;
  }

  function stopIfRunning() {
    clearState();
    hideBadges();
  }

  async function syncWithCart() {
    try {
      const cart = await fetchCart();

      if (!hasAnyTicketItems(cart)) {
        // No ticket items => stop timer
        stopIfRunning();
        return;
      }

      // There is at least one ticket item => ensure timer exists
      ensureStarted();
      showBadges();
    } catch (e) {
      // If cart fetch fails, don't break UI; keep last state
      // console.warn(e);
    }
  }

  async function tick() {
    const state = readState();
    if (!state) {
      hideBadges();
      return;
    }

    const t = now();
    const remainingToSoft = state.softExpiresAt - t;

    // Display only the "public" 20-minute countdown
    setBadgeText(formatTime(remainingToSoft));

    // After hard expiry, remove ticket items once
    if (t >= state.hardExpiresAt && !state.hardActionDone) {
      state.hardActionDone = true;
      writeState(state);

      try {
        await removeTicketItemsFromCart();
      } catch (e) {
        // If removal fails, allow retry by clearing hardActionDone
        state.hardActionDone = false;
        writeState(state);
      }
    }

    // If cart becomes empty of ticket items later, sync loop will clear state
  }

  // ---------- Dawn integration (best-effort) ----------
  function hookDawnEvents() {
    // 1) When our own change happens
    document.addEventListener("pt:cart:changed", () => {
      syncWithCart();
    });

    // 2) Listen to storage changes (multiple tabs)
    window.addEventListener("storage", (ev) => {
      if (ev.key === SETTINGS.storageKey) {
        // update badges immediately
        tick();
      }
    });

    // 3) If Dawn uses pubsub, try to hook (safe)
    // Some Dawn versions expose `subscribe`/`publish` in global scope.
    // We can't rely on it, but polling covers it anyway.
  }

  // ---------- Boot ----------
  async function boot() {
    hookDawnEvents();

    // First sync
    await syncWithCart();
    tick();

    // Poll cart state to catch add/remove actions from anywhere
    setInterval(syncWithCart, SETTINGS.syncCartMs);

    // UI tick (smooth countdown)
    setInterval(tick, SETTINGS.tickMs);
  }

  document.addEventListener("DOMContentLoaded", boot);
})();